"use client";

import React, { useState } from "react";
import { Box, Collapse, Stack, Typography, SxProps } from "@mui/material";

export interface ExpandableListSectionProps {
  title: string;
  items: string[];
  icon?: React.ReactNode;
  itemsToShowInitially?: number;
  enableExpandCollapse?: boolean;
  renderItemContent?: (item: string) => React.ReactNode;
  containerSx?: SxProps;
  testId?: string;
  getItemKey?: (item: string) => string;
  itemsSpacing?: number;
}

const ExpandableListSection = ({
  title,
  items,
  icon,
  itemsToShowInitially = 3,
  enableExpandCollapse = true,
  renderItemContent,
  containerSx,
  testId,
  getItemKey,
  itemsSpacing = 2
}: ExpandableListSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!items || items.length === 0) {
    return null;
  }

  const hasMore = enableExpandCollapse && items.length > itemsToShowInitially;
  const visibleItems = hasMore ? items.slice(0, itemsToShowInitially) : items;
  const hiddenItems = hasMore ? items.slice(itemsToShowInitially) : [];

  const renderListItem = (item: string, index: number) => (
    <Box
      key={getItemKey ? getItemKey(item) : item}
      sx={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}
    >
      {/* Bullet point */}
      <Box
        sx={{
          display: "inline-block",
          width: "3px",
          height: "3px",
          backgroundColor: "common.black",
          borderRadius: "50%",
          mt: 1,
          flexShrink: 0
        }}
      />

      {/* Item content */}
      <Typography data-testid={`${testId}-copy_${index}`} sx={{ fontSize: "1.6rem", fontWeight: 400 }}>
        {renderItemContent ? renderItemContent(item) : item}
      </Typography>
    </Box>
  );

  return (
    <Box
      data-testid={testId}
      sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, ...containerSx }}
    >
      {/* Icon */}
      {icon && (
        <Box
          sx={{
            bgcolor: "background.cool_strong",
            borderRadius: "50%",
            p: "0.8rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}
        >
          {icon}
        </Box>
      )}

      {/* Content */}
      <Stack spacing={2} sx={{ flex: 1 }}>
        {/* Title */}
        <Typography
          variant="h6"
          data-testid={`${testId}-title`}
          sx={{ lineHeight: "1.3 !important", fontWeight: "600 !important" }}
        >
          {title}
        </Typography>

        {/* Items List */}
        <Stack spacing={itemsSpacing} sx={{ pl: 1 }}>
          {visibleItems.map((item, index) => renderListItem(item, index))}

          {hasMore && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Stack spacing={1}>
                {hiddenItems.map((item, index) =>
                  renderListItem(item, visibleItems.length + index)
                )}
              </Stack>
            </Collapse>
          )}
        </Stack>

        {/* Read More/Less Button */}
        {hasMore && (
          <Typography
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid={`${testId}-button`}
            sx={{
              fontSize: "1.4rem",
              fontWeight: 600,
              color: "secondary.main",
              cursor: "pointer",
              mt: 1
            }}
          >
            {isExpanded ? "Read Less" : "Read More"}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default ExpandableListSection;
